import wretch from 'wretch';
import {
  IExpandedRows,
  isMultiHierarchyAccountSummary,
  MH_DETAILS_TRUNCATION_HEADER,
  MhDetailsExportResult
} from '@opengov/reporting-types';
import type {
  EligibleChildGroupsResponse,
  ErpSegmentGroupResponse,
  ErpSegmentGroupsResponse,
  ErpSegmentsResponse,
  GroupHierarchyResponse,
  LeafSegmentCodesResponse,
  SegmentGroupSearchGroupTypeParam,
  SegmentGroupSearchRequest
} from '@opengov/api-deprecated';
// The ERP child-groups client lives beside the hooks that call it rather than in `@opengov/api-deprecated`;
// see the note at the top of that module. Type-only, so no runtime dependency on the hook layer.
import type { DirectChildGroupsResponse } from '../apiHooks/erp/directChildGroups';
import { cacheMiddlewareFactory } from './cacheMiddleware';
import * as Api from './reportingApi.d';
import { generateUniqueKey } from '../utils/uniqueKeyUtils';
import { pick } from 'lodash';
import { REPORTING_SERVICE_V2_PATH } from './constants';
export type { PivotRowsCalculateArgs, GetRollupDimensionFreshnessResult } from './reportingApi.d';

const transport = wretch()
  .options({ credentials: 'same-origin' })
  .accept('application/json')
  .content('application/json')
  .url('/api/reporting_service/next');

// Versioned because the response shape changed (BUD-18823 renamed the leaf-detail metadata flag),
// so responses cached by older bundles must not be read. Bump again on future response-shape
// changes, moving the previous name into `LEGACY_PIVOT_CACHE_NAMESPACES`.
const PIVOT_CACHE_NAMESPACE = 'reporting-lib-api-v2';

// Namespaces written by older bundles. Nothing reads them; they are deleted alongside the current
// namespace so their storage is released.
const LEGACY_PIVOT_CACHE_NAMESPACES: readonly string[] = ['reporting-lib-api'];

const cache = cacheMiddlewareFactory({ cache: PIVOT_CACHE_NAMESPACE });

let freshPerOpenEnabled = false;
let activeGeneration: string | null = null;
let generationCounter = 0;

/**
 * `randomUUID` only exists in secure contexts, so a plain counter is the fallback. Uniqueness only
 * has to hold against earlier generations in this browser, and the timestamp already provides that.
 */
const uniquePart = (webCrypto: Partial<Crypto> | undefined, counter: number): string =>
  webCrypto?.randomUUID?.() ?? counter.toString(36);

const newGenerationId = (): string => {
  generationCounter += 1;
  return `${Date.now().toString(36)}-${uniquePart(globalThis.crypto, generationCounter)}`;
};

/**
 * Deletes the entries that belong to neither `keep` nor the generation active now.
 *
 * Entry-by-entry deletion, unlike `caches.delete(namespace)`, leaves every open cache handle
 * usable, so a prefetch that is still writing cannot lose its responses to the cleanup.
 */
async function removeEntriesOutsideGenerations(keep: string): Promise<void> {
  if (typeof globalThis === 'undefined' || !('caches' in globalThis)) return;
  try {
    const pivotCache = await globalThis.caches.open(PIVOT_CACHE_NAMESPACE);
    const requests = await pivotCache.keys();
    const isKept = (url: string) =>
      url.includes(`/${keep}/`) || (activeGeneration !== null && url.includes(`/${activeGeneration}/`));
    await Promise.all(requests.filter((request) => !isKept(request.url)).map((request) => pivotCache.delete(request)));
  } catch (err) {
    // Best-effort: leftovers are unreachable under the new keys and the next generation retries.
    // eslint-disable-next-line no-console
    console.warn('Failed to remove old pivot cache generations', err);
  }
}

const startPivotCacheGeneration = (): string => {
  const generation = newGenerationId();
  activeGeneration = generation;
  void removeEntriesOutsideGenerations(generation);
  return generation;
};

/**
 * Turns per-open cache generations on or off. The report route loader calls this with the value of
 * the `reporting-performance-poc` flag every time a report is opened.
 *
 * With it on, every pivot row and column cache entry is stored under the generation that was
 * active when it was requested, and every call starts a new generation. Nothing from an earlier
 * generation can ever be served, which is what guarantees a freshly opened report shows fresh data
 * without deleting the whole namespace.
 *
 * It starts a new one even when generations were already on: the report page stays mounted when the
 * report or the saved view changes, so an open cannot be told apart by the page unmounting.
 */
export function setPivotCacheFreshPerOpen(enabled: boolean): void {
  freshPerOpenEnabled = enabled;
  if (enabled) startPivotCacheGeneration();
  else activeGeneration = null;
}

const currentKeyPrefix = (): string | undefined => {
  if (!freshPerOpenEnabled) return undefined;
  return activeGeneration ?? startPivotCacheGeneration();
};

/**
 * Drops every entry in the pivot row/column cache namespace.
 *
 * The cache middleware decides freshness by comparing each entry's HTTP `Date`
 * header against `expireIfOlderThan`. That signal is correct for pure data
 * changes (lens / report-config edits), but breaks for rollup-dimension edits:
 * a stale row response served *after* a rollup edit ends up with `Date >
 * rollupDataUpdatedAt`, so no caller can ever invalidate it via
 * `expireIfOlderThan`.
 *
 * Use this when a strict, key-agnostic eviction is required (e.g. the report
 * grid detects that the rollup dimension's freshness timestamp advanced
 * mid-session). It is a no-op outside browser environments that expose the
 * Cache API.
 *
 * With per-open generations enabled (see `setPivotCacheFreshPerOpen`) it starts a new generation
 * instead: every earlier entry becomes unreachable at once, and the old entries are removed one
 * by one in the background.
 */
export async function clearPivotRowAndColumnCache(): Promise<void> {
  // Answers still waiting for their cache write live in memory, outside the namespace.
  cache.forgetPending();
  if (freshPerOpenEnabled) {
    startPivotCacheGeneration();
    return;
  }
  // `caches` lives on WindowOrWorkerGlobalScope; it's missing in older browsers and in
  // Node-based test/SSR contexts. Detect via `in` so we don't have to widen the type to
  // `any`.
  if (typeof globalThis === 'undefined' || !('caches' in globalThis)) return;
  try {
    const namespaces = [PIVOT_CACHE_NAMESPACE, ...LEGACY_PIVOT_CACHE_NAMESPACES];
    await Promise.all(namespaces.map((namespace) => globalThis.caches.delete(namespace)));
  } catch (err) {
    // Cache deletion is best-effort; failure here only means callers may see
    // a stale cached response on the next request, which the existing
    // `expireIfOlderThan` mechanism can still catch in many scenarios.
    // eslint-disable-next-line no-console
    console.warn('Failed to clear pivot cache namespace', err);
  }
}

/**
 * `entityGroupId` is part of both cache keys because it selects the chart of accounts the query
 * resolves against, and nothing else in the key records it. A combined report and an ordinary one
 * with byte-identical configuration would otherwise hash the same and serve each other's figures —
 * across entities, which is the one mix-up this product cannot have.
 *
 * It is read straight off the args rather than defaulted: `undefined` is dropped by
 * `JSON.stringify`, so an ordinary report keeps the exact key it has today and no existing cache
 * entry is invalidated by adding it.
 */
export const colRequestCacheKey = (args: Api.PivotColArgs): string => {
  const { asOfDate, columnGroups, filters, columnOrder } = args.report;
  const colCacheKey = generateUniqueKey(
    JSON.stringify({ asOfDate, columnGroups, filters, columnOrder, entityGroupId: args.entityGroupId })
  );
  return colCacheKey;
};

export const rowRequestCacheKey = (args: Api.PivotRowArgs): string => {
  const {
    report,
    rowIndex,
    columnValues,
    groupingValues,
    parentQuery,
    parentGroupId,
    expandLeafDetail,
    rowBreakdownLevel,
    limit,
    offset,
    entityGroupId
  } = args;
  const { asOfDate, columnGroups, rowGroups, expandedRows, filters, formatting } = report;
  const rowCacheKey = generateUniqueKey(
    JSON.stringify({
      rowGroups,
      asOfDate,
      columnGroups,
      expandedRows,
      filters,
      formatting: pick(
        formatting,
        'currencyRounding',
        'numberRounding',
        'percentRounding',
        'zeroNumbers',
        'calcColIntersectionMode'
      ),
      rowIndex,
      columnValues,
      groupingValues,
      parentQuery,
      parentGroupId,
      expandLeafDetail,
      rowBreakdownLevel,
      limit,
      offset,
      // See the note on `colRequestCacheKey`: the chart of accounts this resolves against is not
      // otherwise recorded in the key.
      entityGroupId,
      ...(isMultiHierarchyAccountSummary(report)
        ? { showTotalsRows: report.showTotalsRows, showRootLevelBreakdowns: report.showRootLevelBreakdowns }
        : {})
    })
  );

  return rowCacheKey;
};

export const pivotRows = (args: Api.PivotRowArgs, opts?: Api.PivotRowOptions) => {
  return transport
    .middlewares(
      opts?.cache
        ? [
            cache({
              cacheId: rowRequestCacheKey(args),
              expireIfOlderThan: opts.expireIfOlderThan,
              keyPrefix: currentKeyPrefix(),
              skipLookup: opts.skipCacheLookup
            })
          ]
        : []
    )
    .url('/pivot/rows')
    .post(args)
    .json<Api.PivotRowResult>();
};

export const pivotCols = (args: Api.PivotColArgs, opts?: Api.PivotColOptions) => {
  let request = transport.middlewares(
    opts?.cache
      ? [
          cache({
            cacheId: colRequestCacheKey(args),
            expireIfOlderThan: opts.expireIfOlderThan,
            keyPrefix: currentKeyPrefix()
          })
        ]
      : []
  );

  if (opts?.accessToken) {
    request = request.auth(`Bearer ${opts.accessToken}`);
  }

  return request.url('/pivot/columns').post(args).json<Api.PivotColResult>();
};

/**
 * MH Details table — the rows of a column group's lens that sit under the report's row hierarchy.
 *
 * Kept on its own cache namespace rather than `PIVOT_CACHE_NAMESPACE`, so
 * `clearPivotRowAndColumnCache` and Details invalidation stay independent. Both are cleared on the
 * same rollup-dimension freshness signal; sharing a namespace would mean a pivot-only eviction also
 * dropped Details, and vice versa.
 */
const DETAILS_CACHE_NAMESPACE = 'reporting-lib-details';

const detailsCache = cacheMiddlewareFactory({ cache: DETAILS_CACHE_NAMESPACE });

export async function clearMhDetailsCache(): Promise<void> {
  detailsCache.forgetPending();
  if (typeof globalThis === 'undefined' || !('caches' in globalThis)) return;
  try {
    await globalThis.caches.delete(DETAILS_CACHE_NAMESPACE);
  } catch (err) {
    // Best-effort, as with the pivot namespace: a failure here only risks serving a stale entry
    // that `expireIfOlderThan` can still catch.
    // eslint-disable-next-line no-console
    console.warn('Failed to clear MH details cache namespace', err);
  }
}

export const detailsRequestCacheKey = (args: Api.MhDetailsArgs): string => {
  // `columnValues` is deliberately absent: the table no longer pivots, so it is accepted and
  // ignored (see `MhDetailsArgs`) and must not key the cache.
  const {
    report,
    limit,
    offset,
    sort,
    filters,
    showTotals,
    visibleColumnKeys,
    groupBy,
    groupKeys,
    columnGroupId,
    entityGroupId
  } = args;
  const { asOfDate, columnGroups, rowGroups, filters: reportFilters, columnOrder } = report;

  return generateUniqueKey(
    JSON.stringify({
      asOfDate,
      columnGroups,
      rowGroups,
      reportFilters,
      columnOrder,
      columnGroupId,
      // The server selects only the visible columns, and a grouped request returns group rows or one
      // group's children — each shape is a different response.
      visibleColumnKeys,
      groupBy,
      groupKeys,
      limit,
      offset,
      sort,
      filters,
      // Both change the response, so both belong in the key — the same two the rows key carries for a
      // multi-hierarchy report.
      //
      // `showTotals` adds `metadata.totals`, which is what the grid renders as its pinned bottom row.
      // Leaving it out meant turning the toggle on replayed a cached totals-free page for up to ten
      // minutes, and no amount of refetching would produce a totals row.
      //
      // `showRootLevelBreakdowns` decides whether the walk expands root-level breakdown rows, so it
      // changes the row set itself, not just the metadata.
      showTotals,
      showRootLevelBreakdowns: report.showRootLevelBreakdowns,
      // Same reasoning as the two pivot keys above, and it bites hardest here: Details renders leaf
      // accounts, so a collision serves one family's individual account rows under another's report.
      // Undefined for an ordinary report, which `JSON.stringify` drops, so existing keys are intact.
      entityGroupId
    })
  );
};

export const pivotDetails = (args: Api.MhDetailsArgs, opts?: Api.PivotRowOptions) =>
  transport
    .middlewares(
      opts?.cache
        ? [detailsCache({ cacheId: detailsRequestCacheKey(args), expireIfOlderThan: opts.expireIfOlderThan })]
        : []
    )
    .url('/pivot/details')
    .post(args)
    .json<Api.MhDetailsResult>();

/** Returns the CSV body; the caller hands it to `browserDownload`. Never cached. */
export const pivotDetailsExport = (args: Api.MhDetailsArgs): Promise<MhDetailsExportResult> =>
  transport
    .accept('text/csv')
    .url('/pivot/details/export')
    .post(args)
    .res(async (response) => {
      const truncationReason = response.headers.get(MH_DETAILS_TRUNCATION_HEADER);
      return {
        csv: await response.text(),
        ...(truncationReason === 'exportCap' ? { truncationReason } : {})
      };
    });

export const getDatasetSchemas = (args: Api.GetDatasetSchemasArgs) =>
  transport.url('/dataset/schema').post(args).json<Api.GetDatasetSchemasResult>();

export const getDistinctDatasetColumnValues = (
  { filter, ...args }: Api.GetDistinctDatasetColumnValuesArgs,
  opts?: Api.GetDistinctDatasetColumnValuesOptions
) => {
  let request = transport
    .middlewares(opts?.cache ? [cache()] : [])
    .url(`/dataset/${encodeURIComponent(args.id)}/column/${args.column}/distinct`);

  const queryParams = new URLSearchParams();

  if (filter.length) {
    queryParams.set('filter', filter);
  }

  if (args.reportConfigurationId) {
    queryParams.set('reportConfigurationId', args.reportConfigurationId);
  }

  if (queryParams.size > 0) {
    request = request.url('?' + queryParams.toString());
  }

  return request.get().json<Api.GetDistinctDatasetColumnValuesResult>();
};

export function pivotRowsCalculate(args: Api.PivotRowsCalculateArgs) {
  return transport.url('/pivot/rows/calculate').post(args).json<Api.PivotRowResult>();
}

export function getRollupDimensionFreshness(entitySubdomain: string): Promise<Api.GetRollupDimensionFreshnessResult> {
  return transport
    .url(`/pivot/rollup-dimension/freshness?entitySubdomain=${encodeURIComponent(entitySubdomain)}`)
    .get()
    .json<Api.GetRollupDimensionFreshnessResult>();
}

// Minimal shapes the reporting-service ERP proxies actually return for the public path. The proxy
// handlers (erpSegmentsRouteHandler / erpSegmentGroupsRouteHandler) intentionally trim the upstream ERP
// payload to just these fields so the public, unauthenticated endpoints never over-expose ERP data.
// These mirror the server-side Public* types; the client must NOT claim the full ErpSegment(Group) shape,
// or future callers would type-check against fields that are `undefined` at runtime on the public path.
export type PublicErpSegment = { id: number; displayName: string; active: boolean };
export type PublicErpSegmentsResponse = { data: PublicErpSegment[] };
export type PublicErpSegmentGroup = { id: number; name: string };
export type PublicErpSegmentGroupsResponse = { data: { data: PublicErpSegmentGroup[] } };
export type PublicErpSegmentCode = { code: string; description: string | null };
export type PublicErpSegmentCodesResponse = { data: PublicErpSegmentCode[] };

// Proxies ERP `/segments` through reporting-service so public (transparency) viewers can load
// multi-hierarchy CoA filter options without an Auth0 token. reporting-service authenticates to ERP
// with its own service-account credentials, but only after validating that the caller may view
// `reportConfigurationId` (public, for anonymous callers); the ERP entity is derived from that report.
export function getErpSegmentsViaReportingService(reportConfigurationId: string): Promise<PublicErpSegmentsResponse> {
  return transport
    .url(`/pivot/erp/segments?reportConfigurationId=${encodeURIComponent(reportConfigurationId)}`)
    .get()
    .json<PublicErpSegmentsResponse>();
}

// Proxies ERP segment-group search through reporting-service for the public "Group" filter dropdown.
// Same report-gated, service-account-authenticated path as getErpSegmentsViaReportingService.
export function getErpSegmentGroupsViaReportingService(
  reportConfigurationId: string,
  segmentId: number,
  groupType?: SegmentGroupSearchGroupTypeParam
): Promise<PublicErpSegmentGroupsResponse> {
  const params = new URLSearchParams({ reportConfigurationId });
  if (groupType) {
    params.set('groupType', groupType);
  }
  return transport
    .url(`/pivot/erp/segments/${encodeURIComponent(segmentId)}/groups?${params.toString()}`)
    .get()
    .json<PublicErpSegmentGroupsResponse>();
}

// Proxies a segment's leaf codes through reporting-service for the public "Code" / "Code Description"
// filter dropdowns. Same report-gated, service-account-authenticated path as the groups proxy above;
// the response is already de-duplicated and sorted.
export function getErpSegmentCodesViaReportingService(
  reportConfigurationId: string,
  segmentId: number
): Promise<PublicErpSegmentCodesResponse> {
  const params = new URLSearchParams({ reportConfigurationId });
  return transport
    .url(`/pivot/erp/segments/${encodeURIComponent(segmentId)}/codes?${params.toString()}`)
    .get()
    .json<PublicErpSegmentCodesResponse>();
}

const v2Transport = wretch()
  .options({ credentials: 'same-origin' })
  .accept('application/json')
  .content('application/json')
  .url(REPORTING_SERVICE_V2_PATH);

export function exportAccountSummaryCsv(args: {
  reportConfigurationId: string;
  expandedRows: IExpandedRows;
}): Promise<string> {
  return v2Transport.accept('text/csv').url('/exportAccountSummary').post(args).text();
}

export type EntityGroupMember = { uuid: string; label: string };

/**
 * Why the answer is what it is. Mirrors `EntityGroupMembersReason` in reporting-service's
 * `entityGroupRoutes`, which is the spec — every resolution step there is a server-to-server call
 * that never reaches browser devtools, so a single-entity answer is otherwise indistinguishable
 * from a missing claim, a deactivated group, or a subdomain the caller does not hold.
 */
export type EntityGroupMembersReason =
  /** The caller may build across the family. */
  | 'parent'
  /** No `entitySubdomain` on the request, so there is no entity to resolve a family for. */
  | 'noActiveSubdomain'
  /** That entity is not available to this caller. Collapses two causes, deliberately. */
  | 'entityUnavailable'
  /** The entity belongs to no group — the ordinary case. */
  | 'standalone'
  /** In a group, but the entity does not lead it. */
  | 'notFamilyParent'
  /** A member entity could not be resolved, so the roster would have been incomplete. */
  | 'memberUnresolved'
  /** Entity-service refused reporting-service's own account — a deployment gap, answered single-entity. */
  | 'entityGroupsUnavailable';

export type EntityGroupMembersResult = {
  isMultiEntity: boolean;
  /** Member entity uuids, including the caller's own. Empty when not multi-entity. */
  entityUuids: string[];
  /** Same members with a display label, for showing which entity a group came from. */
  entities: EntityGroupMember[];
  /**
   * The family this answer describes, recorded on a combined report as `report.entityGroupId`.
   *
   * Optional because the server only sends it for `reason: 'parent'` — every other reason is a
   * single-entity answer with no family to name.
   */
  groupId?: string;
  reason: EntityGroupMembersReason;
};

/**
 * The entities the current user may build a combined report across.
 *
 * Single-entity customers get `isMultiEntity: false` and an empty list, which keeps the builder
 * on its existing single-entity path.
 */
export function getMyEntityGroup(entitySubdomain: string): Promise<EntityGroupMembersResult> {
  return v2Transport
    .url(`/entity_groups/me?entitySubdomain=${encodeURIComponent(entitySubdomain)}`)
    .get()
    .json<EntityGroupMembersResult>();
}

/**
 * ERP chart-of-accounts reads for a *member* entity, proxied by reporting-service.
 *
 * Every ERP call is scoped to one entity, and a browser token carries claims for a single entity —
 * so the multi-entity picker's fan-out to another member is unauthorized. ERP answers a group it
 * cannot see with an empty 200 rather than an error, so calling it directly loses that member's
 * chart of accounts with nothing on screen to say so. The service has no such limit: it
 * authenticates to ERP with a service-account key, and authorizes the requested `entityUuid`
 * against a roster derived from the caller's own token claims (403 outside it).
 *
 * Each response is the raw ERP payload, so callers keep the ERP response types they already use —
 * only the transport changes. No entity subdomain is sent: the server ignores one.
 *
 * Single-entity callers keep calling ERP directly; see `resolveErpTransport`.
 */
type MemberEntityErpOperation =
  | 'segments'
  | 'searchGroups'
  | 'groupById'
  | 'groupHierarchy'
  | 'childGroups'
  | 'eligibleChildGroups'
  | 'leafSegmentCodes';

/** Every operation takes the entity it reads in the body, alongside its own parameters. */
function postMemberEntityErp<TResponse>(
  operation: MemberEntityErpOperation,
  body: Record<string, unknown>
): Promise<TResponse> {
  return v2Transport.url(`/multi_entity_erp/${operation}`).post(body).json<TResponse>();
}

export function getMemberEntityErpSegments(entityUuid: string): Promise<ErpSegmentsResponse> {
  return postMemberEntityErp('segments', { entityUuid });
}

export function searchMemberEntitySegmentGroups(params: {
  entityUuid: string;
  segmentId: number;
  groupType?: SegmentGroupSearchGroupTypeParam;
  body: SegmentGroupSearchRequest;
}): Promise<ErpSegmentGroupsResponse> {
  return postMemberEntityErp('searchGroups', params);
}

export function getMemberEntitySegmentGroupById(params: {
  entityUuid: string;
  segmentId: number;
  groupId: number;
}): Promise<ErpSegmentGroupResponse> {
  return postMemberEntityErp('groupById', params);
}

export function getMemberEntityGroupHierarchy(params: {
  entityUuid: string;
  groupId: number;
}): Promise<GroupHierarchyResponse> {
  return postMemberEntityErp('groupHierarchy', params);
}

/**
 * Unlike its siblings this payload is not post-processed here: the direct client flattens and sorts
 * ERP's child-group data, so the caller applies the same normalization to keep child order
 * identical whichever transport served it.
 */
export function getMemberEntityChildGroups(params: {
  entityUuid: string;
  groupId: number;
}): Promise<DirectChildGroupsResponse> {
  return postMemberEntityErp('childGroups', params);
}

export function getMemberEntityEligibleChildGroups(params: {
  entityUuid: string;
  segmentId: number;
  groupId: number;
}): Promise<EligibleChildGroupsResponse> {
  return postMemberEntityErp('eligibleChildGroups', params);
}

export function getMemberEntityLeafSegmentCodes(params: {
  entityUuid: string;
  leafGroupIds: readonly number[];
}): Promise<LeafSegmentCodesResponse> {
  return postMemberEntityErp('leafSegmentCodes', params);
}
